/**
 * VariationsSettingsPanel
 *
 * Right panel settings for image variation generation.
 * User context, ratio, resolution, image count, format, cost display.
 * Pricing loaded dynamically from DB (feature_catalog).
 */

import { useState, useMemo } from 'react';
import { Wand2, Zap, AlertTriangle, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Select, Textarea, Button, Badge, Alert, AlertDescription } from '@/components/v2/ui';
import { useWorkspaceConfig } from '@/hooks/useWorkspaceConfig';
import { useVariationsPricing } from '@/hooks/useVariations';
import { formatNumber } from '@/lib/utils';

const RATIOS = [
  { value: '1:1', label: '1:1 (Quadrat)' },
  { value: '4:3', label: '4:3 (Landschaft)' },
  { value: '3:4', label: '3:4 (Portrait)' },
  { value: '16:9', label: '16:9 (Breitbild)' },
  { value: '9:16', label: '9:16 (Story)' },
];

const RESOLUTIONS = [
  { value: '1k', label: '1K (1024x1024)' },
  { value: '2k', label: '2K (2048x2048)' },
  { value: '4k', label: '4K (4096x4096)' },
];

const FORMATS = [
  { value: 'png', label: 'PNG' },
  { value: 'jpg', label: 'JPG' },
  { value: 'webp', label: 'WebP' },
];

interface VariationsSettingsPanelProps {
  onGenerate: (settings: VariationsSettings) => void;
  isGenerating: boolean;
  hasImage: boolean;
}

export interface VariationsSettings {
  user_context: string;
  ratio: string;
  resolution: string;
  image_count: number;
  image_format: string;
  png_quality: string;
}

export function VariationsSettingsPanel({
  onGenerate,
  isGenerating,
  hasImage,
}: VariationsSettingsPanelProps) {
  const { t } = useTranslation();
  const [userContext, setUserContext] = useState('');
  const [ratio, setRatio] = useState('1:1');
  const [resolution, setResolution] = useState('1k');
  const [imageCount, setImageCount] = useState(1);
  const [imageFormat, setImageFormat] = useState('png');

  const { config } = useWorkspaceConfig();
  const credits = config?.user_credits ?? 0;
  const { data: pricing, isLoading: pricingLoading } = useVariationsPricing();

  const perImage = useMemo(() => {
    if (!pricing) return 0;
    return resolution === '4k' ? pricing.per_image_4k : pricing.per_image;
  }, [pricing, resolution]);

  const totalCost = perImage * imageCount;
  const hasEnoughCredits = credits >= totalCost;
  const canSubmit = hasImage && userContext.trim().length > 0 && !isGenerating && hasEnoughCredits && !pricingLoading;

  const handleSubmit = () => {
    onGenerate({
      user_context: userContext,
      ratio,
      resolution,
      image_count: imageCount,
      image_format: imageFormat,
      png_quality: 'web',
    });
  };

  return (
    <div className="p-4 space-y-4">
      <h3 className="text-sm font-semibold text-neutral-900 dark:text-neutral-100">
        Variations-Einstellungen
      </h3>

      <Textarea
        label={t('generation.promptLabel')}
        placeholder={t('generation.additionalInstructionsPlaceholder')}
        rows={4}
        value={userContext}
        onChange={(e) => setUserContext(e.target.value)}
        helperText={t('generation.additionalInstructions')}
      />

      <Select
        label={t('generation.aspectRatioLabel')}
        options={RATIOS}
        value={ratio}
        onValueChange={setRatio}
      />

      <Select
        label={t('generation.resolutionLabel')}
        options={RESOLUTIONS}
        value={resolution}
        onValueChange={setResolution}
        helperText={pricing ? `${perImage} Credits pro Bild` : undefined}
      />

      {/* Image Count Slider */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <label className="text-sm font-medium text-neutral-700 dark:text-neutral-300">
            Anzahl Bilder
          </label>
          <span className="text-sm font-semibold text-orange-600 dark:text-orange-400">
            {imageCount}
          </span>
        </div>
        <input
          type="range"
          min="1"
          max="4"
          step="1"
          value={imageCount}
          onChange={(e) => setImageCount(Number(e.target.value))}
          className="w-full h-2 bg-neutral-200 dark:bg-neutral-700 rounded-lg appearance-none cursor-pointer accent-orange-500"
        />
      </div>

      <Select
        label="Bildformat"
        options={FORMATS}
        value={imageFormat}
        onValueChange={setImageFormat}
      />

      {/* Cost Display */}
      <div className="flex items-center justify-between p-3 rounded-lg bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-950/30 dark:to-orange-950/30 border border-amber-200 dark:border-amber-800">
        <div className="flex items-center gap-2">
          <Zap className="h-4 w-4 text-amber-600 dark:text-amber-400" />
          <span className="text-sm font-medium text-neutral-700 dark:text-neutral-300">
            {t('generation.costLabel')}
          </span>
        </div>
        {pricingLoading ? (
          <Loader2 className="h-4 w-4 animate-spin text-neutral-400" />
        ) : (
          <Badge variant={hasEnoughCredits ? 'default' : 'error'} className="font-bold">
            {formatNumber(totalCost)} Credits
          </Badge>
        )}
      </div>

      {!hasEnoughCredits && !pricingLoading && (
        <Alert variant="warning">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            <strong>{t('generation.notEnoughCredits')}</strong>{' '}
            <a href="/v2/credits" className="underline font-medium">
              Jetzt aufladen
            </a>
          </AlertDescription>
        </Alert>
      )}

      <Button
        type="button"
        variant="primary"
        className="w-full"
        disabled={!canSubmit}
        loading={isGenerating}
        onClick={handleSubmit}
      >
        {isGenerating ? (
          t('common.generating')
        ) : (
          <>
            <Wand2 className="h-4 w-4 mr-2" />
            {t('common.generate')}
          </>
        )}
      </Button>
    </div>
  );
}
